/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * De configuratie van de applicatie: change detection, http, de vertalingen, de localconfig, de router
 * met de hash-routes, de taalbepaling, en de services die bij het opstarten moeten bestaan.
 *
 * Tot golf 4f waren dit drie lagen, voor twee ingangen: de eilanden in de AngularJS-pagina en de shell.
 * Er is nu één ingang, dus één configuratie (plan-golf-4f.md, B11).
 */
import {
    ApplicationConfig,
    inject,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZoneChangeDetection
} from '@angular/core';
import {provideHttpClient} from '@angular/common/http';
import {provideRouter, withHashLocation} from '@angular/router';
import {provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import {provideTranslateHttpLoader, TranslateHttpLoader} from '@ngx-translate/http-loader';
import {LOCAL_CONFIG, LocalConfig} from 'sihw-ngx';
import {TAALALIASSEN, TALEN_TOKEN} from 'dynalearn-common/services/taal.tokens';
import {TaalService} from 'dynalearn-common/services/taal.service';
import {localconfig} from './localconfig';
import {routes} from './routes';
import {ChatService} from '../services/chat.service';
import {ApiService} from './backend/api.service';
import {SamenwerkprotocolService} from './samenwerking/samenwerkprotocol.service';
import {SimulatieService} from './simulatie/simulatie.service';
import {Dashboardv1Service} from '../services/dashboardv1.service';
import {Dashboardv2Service} from '../services/dashboardv2.service';
import {WeergaveInstellingenService} from '../services/weergave-instellingen.service';
import {SamenwerkingService} from '../services/samenwerking.service';

/**
 * De talen waarin create beschikbaar is; van elk staat een bestand in `public/locale/`. De eerste is de
 * terugvaltaal. De backoffice-applicaties hebben er drie, dus de lijst is per applicatie injecteerbaar.
 */
const TALEN = ['en', 'nl', 'de', 'fr', 'pt-BR', 'el'] as const;

/**
 * Taalcodes die op een andere taal uitkomen. De browsertaal komt als basistaal binnen (`pt`), en create
 * heeft alleen `pt-BR`; zonder deze tabel valt een Braziliaanse bezoeker terug op Engels. Elke `pt` gaat
 * naar `pt-BR` — de doelgroep zit in Brazilië, niet in Portugal (besluit 08-09-2026), en dat deed de
 * ng1-configuratie ook (`'pt-*'` én `'pt'`).
 */
const TAALALIASTABEL = {pt: 'pt-BR'};

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection({eventCoalescing: true}),
        provideBrowserGlobalErrorListeners(),
        provideHttpClient(),
        //ngx-bootstrap 21.2: BsModalService is providedIn: 'root', ModalModule.forRoot() bestaat niet meer
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateHttpLoader
            },
            fallbackLang: 'en'
        }),
        provideTranslateHttpLoader({
            prefix: './locale/',
            suffix: '.json'
        }),

        //localconfig
        {provide: LOCAL_CONFIG, useValue: localconfig},
        provideAppInitializer(async () => {
            await LocalConfig.init(inject(LOCAL_CONFIG) as any);
        }),
        provideAppInitializer(() => {
            //de backendlaag hier al aanmaken, zodat de socket opgezet is voordat er iets gevraagd wordt
            inject(ApiService);
        }),
        provideAppInitializer(() => {
            //de chat luistert in zijn constructor op de notify-stroom; zonder deze injectie bestaat hij pas
            //als het eiland getekend wordt en zouden er berichten gemist worden
            inject(ChatService);
        }),

        provideRouter(routes, withHashLocation()),
        {provide: TALEN_TOKEN, useValue: TALEN},
        {provide: TAALALIASSEN, useValue: TAALALIASTABEL},
        provideAppInitializer(() => normaliseerHash()),
        provideAppInitializer(() => inject(TaalService).start()),

        provideAppInitializer(() => {
            //Deze services doen werk in hun constructor: ze luisteren op de notify-stroom, registreren
            //zich bij een andere service of zetten een effect. Tot golf 4f maakte de migratie-interface
            //ze in de constructor van `App` aan, omdat hij ze allemaal injecteerde; zonder deze
            //initializer bestonden ze pas zodra het bouwscherm ze nodig heeft, en dan is een notify die
            //eerder binnenkwam gemist (plan-golf-4f.md, B8). Als laatste, zodat de volgorde dezelfde
            //blijft: na de localconfig en de taal
            inject(SamenwerkprotocolService);
            inject(SimulatieService);
            inject(Dashboardv1Service);
            inject(Dashboardv2Service);
            inject(WeergaveInstellingenService);
            inject(SamenwerkingService);
        })
    ]
};

/**
 * Zet een url van de vorm `#!/pad` om naar `#/pad`, zodat de adresbalk klopt bij het openen van een van
 * de vier contract-url's. Dit draait één keer; een hashwijziging in een tab die al openstaat vangt de
 * `**`-route op (zie `routes.ts`).
 */
function normaliseerHash(): void {
    if (location.hash.startsWith('#!/')) {
        history.replaceState(null, '', `${location.pathname}${location.search}#${location.hash.slice(2)}`);
    }
}
